import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getMotivationInterpretation } from '../data/motivationQuestions';
import { Target, ArrowRight, Home, RefreshCw, Sparkles, TrendingUp } from 'lucide-react';

export const MotivationResults = () => {
  const location = useLocation();
  const { language, t } = useLanguage();
  const result = location.state?.result;

  if (!result) {
    return <Navigate to="/dashboard" replace />;
  }

  const interpretation = getMotivationInterpretation(result.total_score, language);
  const isHighMotivation = result.total_score >= 10;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="motivation-results">
      <div className="max-w-3xl mx-auto">
        {/* Result Card */}
        <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-xl animate-scale-in" data-testid="result-card">
          {/* Header with Score */}
          <div className={`${interpretation.bgColor} p-8 sm:p-12 text-center`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full mb-6">
              <Target className={`w-5 h-5 ${interpretation.textColor}`} />
              <span className={`font-medium ${interpretation.textColor}`}>
                {language === 'fr' ? 'Votre motivation' : 'Your motivation'}
              </span>
            </div>

            <div className={`text-7xl sm:text-8xl font-extrabold ${interpretation.textColor} mb-4`}>
              {result.total_score}
              <span className="text-3xl font-normal opacity-60">/16</span>
            </div>

            <h2 className={`text-2xl sm:text-3xl font-bold ${interpretation.textColor} mb-4`}>
              {interpretation.level}
            </h2>
          </div>

          {/* Interpretation */}
          <div className="p-8 sm:p-12">
            <div className="flex items-start gap-4 mb-8">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                isHighMotivation ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                {isHighMotivation ? (
                  <Sparkles className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {language === 'fr' ? 'Interprétation' : 'Interpretation'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {interpretation.message}
                </p>
              </div>
            </div>

            {/* Motivation Tips */}
            <div className="bg-purple-50 rounded-2xl p-6 mb-8">
              <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                {language === 'fr' ? 'Conseils pour renforcer votre motivation' : 'Tips to strengthen your motivation'}
              </h4>
              <ul className="space-y-3 text-purple-800">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  {language === 'fr' 
                    ? 'Notez vos raisons personnelles d\'arrêter de fumer'
                    : 'Write down your personal reasons to quit smoking'}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  {language === 'fr' 
                    ? 'Fixez-vous une date d\'arrêt et tenez-vous y'
                    : 'Set a quit date and stick to it'}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  {language === 'fr' 
                    ? 'Parlez de votre projet à votre entourage'
                    : 'Tell your family and friends about your plan'}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  {language === 'fr' 
                    ? 'Calculez l\'argent que vous économiserez'
                    : 'Calculate the money you will save'}
                </li>
              </ul>
            </div>

            {/* Score Scale */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">
                {language === 'fr' ? 'Échelle de motivation' : 'Motivation scale'}
              </h4>
              <div className="space-y-3">
                {[
                  { range: '14-16', label_fr: 'Très élevée', label_en: 'Very high', color: 'bg-green-100 text-green-800' },
                  { range: '10-13', label_fr: 'Bonne', label_en: 'Good', color: 'bg-green-200 text-green-900' },
                  { range: '6-9', label_fr: 'Moyenne', label_en: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
                  { range: '3-5', label_fr: 'Faible', label_en: 'Low', color: 'bg-orange-100 text-orange-800' },
                  { range: '0-2', label_fr: 'Très faible', label_en: 'Very low', color: 'bg-red-100 text-red-900' },
                ].map((level, index) => {
                  const [min, max] = level.range.split('-').map(Number);
                  const isActive = result.total_score >= min && result.total_score <= max;
                  
                  return (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-3 rounded-xl ${
                        isActive ? level.color + ' font-medium' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span>{language === 'fr' ? level.label_fr : level.label_en}</span>
                      <span className="font-mono">{level.range}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dashboard" className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-full border-2"
                  data-testid="back-dashboard-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('backToDashboard')}
                </Button>
              </Link>
              <Link to="/motivation-test" className="flex-1">
                <Button 
                  className="w-full h-12 rounded-full bg-purple-600 hover:bg-purple-700 shadow-lg"
                  data-testid="retake-test-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Refaire le test' : 'Retake test'}
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
